import * as signalR from '@microsoft/signalr';
import { ILogger, LogLevel } from '@microsoft/signalr';
import { useQuery } from '@tanstack/react-query';
import { debounce, isEmpty } from 'lodash-es';
import React, {
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSet } from 'react-use';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { ContentId } from 'src/utils/constants/contentId';
import { ContentIds } from 'src/utils/constants/contentIdDataMap';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { FormatContentIds } from 'src/utils/constants/formatContentIdDataMap';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  Marketplace,
  Notification,
  NotificationClient,
  NotificationState,
  NotificationType,
  Permission,
  UserSetting,
} from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { isContentId, useContentContext } from '../ContentContext';
import { POSSignalRHub } from '../SignalRConnection';
import { fireNativeNotification } from './NativeNotification';

export class SignalRLogger implements ILogger {
  log(logLevel: LogLevel, message: string) {
    if (logLevel <= LogLevel.Debug) {
      return;
    }
    if (logLevel <= LogLevel.Information) {
      console.debug(message);
    } else if (logLevel <= LogLevel.Warning) {
      console.warn(message);
    } else if (logLevel <= LogLevel.Critical) {
      console.error(message);
    }
  }
}

export type NotificationFilter = {
  type?: Set<NotificationType>;
};

type INotificationsContext = {
  notifications: Notification[];
  notificationsIsLoading: boolean;
  notificationsIsInitialLoading: boolean;
  dismissNotification: (notificationId: number) => Promise<void>;
  setNotificationFilter: Dispatch<NotificationFilter>;
  /**
   * Whether a filter is set, but there are no results for the filter.
   */
  noResultsForNotificationFilter: boolean;
};

export const NotificationsContext = React.createContext<INotificationsContext>({
  notifications: [],
  notificationsIsLoading: false,
  notificationsIsInitialLoading: false,
  dismissNotification: async () => {},
  setNotificationFilter: () => {},
  noResultsForNotificationFilter: false,
});

export const useNotificationsContext = () => useContext(NotificationsContext);

export const NotificationsContextProvider = ({
  loadActiveNotifications: loadActiveNotificationsFromProps,
  children,
}: {
  loadActiveNotifications?: boolean;
  children: React.ReactNode;
}) => {
  const { activeAccountWebClientConfig } = useAppContext();

  const hasActiveNotificationsFeature = useUserHasFeature(
    Feature.HomePageNotifications
  );
  const canViewNotifications = useUserHasAnyOfPermissions(
    Permission.General_ViewNotifications
  );
  const { value: enableNotifications } = useServerUserSetting<boolean>({
    id: UserSetting.EnableNotifications,
  });

  const notificationsIsEnabled = useMemo(
    () => enableNotifications === true,
    [enableNotifications]
  );

  const loadActiveNotifications = useMemo(
    () =>
      loadActiveNotificationsFromProps &&
      hasActiveNotificationsFeature &&
      canViewNotifications,
    [
      canViewNotifications,
      hasActiveNotificationsFeature,
      loadActiveNotificationsFromProps,
    ]
  );

  const { trackError, logEvent } = useClientLogContext();

  const { contentResolver, formattedContentResolver } = useContentContext();

  const hubConnection = useRef<POSSignalRHub>();
  const [dismissedNotifications, { add: addDismissedNotification }] =
    useSet<number>();

  const [notificationFilter, setNotificationFilter] =
    useState<NotificationFilter>();

  const resolveContent = useCallback(
    (
      contentId: ContentId | FormatContentId,
      marketplace: Marketplace | null
    ): string | string[] | undefined => {
      return isContentId(contentId)
        ? contentResolver({ ...ContentIds[contentId] })
        : (formattedContentResolver(
            FormatContentIds[contentId].id,
            [marketplace ?? ''],
            FormatContentIds[contentId].defaultValue
          ) as string);
    },
    [contentResolver, formattedContentResolver]
  );

  const shouldQuery = useMemo(() => {
    return loadActiveNotifications && notificationsIsEnabled;
  }, [loadActiveNotifications, notificationsIsEnabled]);

  const getActiveNotificationsQuery = useQuery({
    queryKey: [
      'NotificationsClient.getActiveNotifications',
      activeAccountWebClientConfig.activeAccountId,
      loadActiveNotifications,
    ],
    queryFn: () => {
      if (!shouldQuery) {
        return null;
      }
      return new NotificationClient(
        activeAccountWebClientConfig
      ).getActiveNotifications();
    },
    enabled: shouldQuery,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('NotificationsClient.getActiveNotifications', error);
      },
    },
  });

  const undismissedNotifications = useMemo(() => {
    if (!getActiveNotificationsQuery.data) {
      return [];
    }
    return getActiveNotificationsQuery.data
      .filter((n) => !dismissedNotifications.has(n.notificationId))
      .toSorted((a, b) => b.createDateTime.localeCompare(a.createDateTime));
  }, [dismissedNotifications, getActiveNotificationsQuery.data]);

  const filteredNotifications = useMemo(() => {
    return undismissedNotifications.filter((n) => {
      if (
        notificationFilter?.type &&
        !notificationFilter.type.has(n.notificationType)
      ) {
        return false;
      }
      return true;
    });
  }, [undismissedNotifications, notificationFilter]);

  const getNotificationById = useCallback(
    async (notificationId: number): Promise<Notification | null> => {
      if (!activeAccountWebClientConfig.activeAccountId) {
        return null;
      }
      return tryInvokeApi(
        async () => {
          return new NotificationClient(
            activeAccountWebClientConfig
          ).getNotification(notificationId);
        },
        (error: ErrorTypes) => {
          trackError('NotificationClient.getNotificationById', error, {
            notificationId,
          });
        }
      );
    },
    [activeAccountWebClientConfig, trackError]
  );

  const getNotificationsQueryDebounced = debounce(
    getActiveNotificationsQuery.refetch,
    2000
  );

  useEffect(() => {
    if (!notificationsIsEnabled) {
      if (
        hubConnection.current &&
        hubConnection.current.state === signalR.HubConnectionState.Connected
      ) {
        const id = hubConnection.current.connectionId;
        hubConnection.current.stop().then(() => {
          console.debug(
            'NotificationContext - Notifications is disabled in user settings...stopping Notification connection with id: ' +
              id
          );
        });
        hubConnection.current = undefined;
      }
      return;
    }

    if (!hubConnection.current) {
      hubConnection.current = new POSSignalRHub(
        '/hubs/notification',
        activeAccountWebClientConfig,
        logEvent
      );

      hubConnection.current.on('new', async (message) => {
        try {
          const messageList = JSON.parse(message);
          for (const m of messageList) {
            const notification = await getNotificationById(m.NotificationId);

            notification &&
              notification.notificationState == NotificationState.Active &&
              fireNativeNotification(notification, resolveContent);
          }
          await getNotificationsQueryDebounced();
        } catch (e) {
          console.error(
            'NotificationContext - malformed SignalR Message: ' + message
          );
          trackError(
            'NotificationContext.getNotificationById',
            e as ErrorTypes
          );
        }
      });

      hubConnection.current
        .start()
        .then(() => {
          console.debug(
            'NotificationContext - Creating new Notification connection with id: ' +
              hubConnection.current?.connectionId
          );
        })
        .catch(console.error);
    }

    // Close the connection before unmount
    return () => {
      if (
        hubConnection.current &&
        hubConnection.current.state === signalR.HubConnectionState.Connected
      ) {
        const id = hubConnection.current.connectionId;
        hubConnection.current.stop().then(() => {
          console.debug(
            'NotificationContext - Unmounting...stopping Notification connection with id: ' +
              id
          );
        });
        hubConnection.current = undefined;
      }
    };
  }, [
    activeAccountWebClientConfig,
    getNotificationById,
    getNotificationsQueryDebounced,
    logEvent,
    notificationsIsEnabled,
    resolveContent,
    trackError,
  ]);

  const dismissNotification = useCallback(
    async (notificationId: number) => {
      await tryInvokeApi(
        async () => {
          addDismissedNotification(notificationId);
          await new NotificationClient(
            activeAccountWebClientConfig
          ).dismissNotification(notificationId);
          await getNotificationsQueryDebounced();
        },
        (error: ErrorTypes) => {
          trackError('NotificationClient.dismissNotification', error, {
            notificationId,
          });
        }
      );
    },
    [
      activeAccountWebClientConfig,
      addDismissedNotification,
      getNotificationsQueryDebounced,
      trackError,
    ]
  );

  return (
    <NotificationsContext.Provider
      value={{
        notifications: filteredNotifications,
        notificationsIsLoading: getActiveNotificationsQuery.isPending,
        notificationsIsInitialLoading: getActiveNotificationsQuery.isLoading,
        dismissNotification,
        setNotificationFilter,
        noResultsForNotificationFilter:
          !isEmpty(notificationFilter) &&
          undismissedNotifications.length !== 0 &&
          filteredNotifications.length === 0,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
